import { useEffect, useRef, useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { Bell, LogOut, LayoutDashboard, PlusCircle, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth, homeForRole } from "../auth/AuthContext";
import { Logo } from "../components/Navbar";
import api from "../lib/api";

const NAV = {
  customer: [
    { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/portal/new", label: "New Request", icon: PlusCircle },
  ],
  supplier: [{ to: "/supplier", label: "Dashboard", icon: LayoutDashboard, end: true }],
  admin: [{ to: "/admin", label: "Procurement Control", icon: LayoutDashboard, end: true }],
};

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const ref = useRef(null);

  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notifications-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-gold hover:border-gold/40 transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4" />
        {data.unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-gold text-ink text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="notifications-badge">
            {data.unread}
          </span>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-3 w-80 max-h-96 overflow-y-auto bg-surface border border-white/10 rounded-xl shadow-2xl z-50"
            data-testid="notifications-panel"
            data-lenis-prevent
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
              <span className="text-sm font-outfit text-white">Notifications</span>
              <button
                data-testid="notifications-read-all"
                onClick={async () => {
                  await api.post("/notifications/read-all");
                  load();
                }}
                className="text-xs text-white/40 hover:text-gold transition-colors"
              >
                Mark all read
              </button>
            </div>
            {data.items.length === 0 ? (
              <p className="text-sm text-white/40 px-4 py-6 text-center">No notifications yet.</p>
            ) : (
              data.items.map((n) => (
                <div key={n.id} className={`px-4 py-3 border-b border-white/5 ${n.read ? "opacity-55" : ""}`}>
                  <p className="text-sm text-white flex items-center gap-2">
                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-gold shrink-0" />}
                    {n.title}
                  </p>
                  <p className="text-xs text-white/50 mt-1 leading-relaxed">{n.body}</p>
                </div>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const items = NAV[user?.role] || [];

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const nav = (
    <>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          data-testid={`portal-nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          onClick={() => setMenuOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-4 py-3 text-sm transition-colors ${
              isActive ? "bg-gold/10 text-gold border border-gold/30" : "text-white/55 hover:text-white hover:bg-white/5 border border-transparent"
            }`
          }
        >
          <item.icon className="w-4 h-4" />
          {item.label}
        </NavLink>
      ))}
    </>
  );

  return (
    <div className="min-h-screen bg-ink text-white flex" data-testid="portal-layout">
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-white/8 p-6 sticky top-0 h-screen">
        <Link to="/" className="mb-10 block">
          <Logo />
        </Link>
        <nav className="space-y-2 flex-1">{nav}</nav>
        <div className="border-t border-white/8 pt-5">
          <p className="text-xs text-white/40 truncate">{user?.company_name}</p>
          <p className="text-xs text-white/30 truncate mt-0.5">{user?.email}</p>
          <button
            data-testid="portal-logout-btn"
            onClick={doLogout}
            className="mt-4 flex items-center gap-2 text-sm text-white/50 hover:text-red-400 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <div className="sticky top-0 z-40 bg-ink/80 backdrop-blur-xl border-b border-white/8 px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              data-testid="portal-menu-btn"
              className="md:hidden text-white/70 p-1"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
            >
              <LayoutDashboard className="w-5 h-5" />
            </button>
            <span className="text-[11px] tracking-[0.3em] uppercase text-white/40">
              {user?.role === "admin" ? "Ajemi Procurement Control" : user?.role === "supplier" ? "Supplier Portal" : "Customer Portal"}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs text-white/40 hover:text-gold transition-colors hidden sm:block" data-testid="portal-back-to-site">
              ← Website
            </Link>
            <NotificationBell />
          </div>
        </div>
        <main className="flex-1 px-5 md:px-8 py-8 max-w-6xl w-full mx-auto">
          <Outlet />
        </main>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-ink/95 backdrop-blur-xl p-6 md:hidden"
            data-testid="portal-mobile-menu"
          >
            <div className="flex items-center justify-between mb-10">
              <Logo />
              <button onClick={() => setMenuOpen(false)} className="text-white/70 p-2" aria-label="Close menu" data-testid="portal-mobile-menu-close">
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="space-y-2">{nav}</nav>
            <button
              data-testid="portal-mobile-logout-btn"
              onClick={doLogout}
              className="mt-10 flex items-center gap-2 text-sm text-white/50"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
